import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  styled,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { AppDispatch, RootState } from "../../stores";
import { getLastReportDetails } from "../../features/dashboard-module/dashboardAction";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Container = styled(Box)(({ theme }) => ({
  minHeight: "auto",
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    transform: "scale(0.95)",
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Header = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 700,
  color: "#4b5b5d",
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.2rem",
    marginBottom: theme.spacing(3),
  },
}));

const DetailCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #f5f0e7, #fff)",
  borderRadius: 12,
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  padding: theme.spacing(1.5),
  width: "100%",
  maxWidth: "auto",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const DetailText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 500,
  color: "#4b5b5d",
  lineHeight: 1.2,
  marginBottom: theme.spacing(1.1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    marginBottom: theme.spacing(1),
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #f5f0e7, #fff)",
  borderRadius: 12,
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  padding: theme.spacing(1.5),
  width: "50%",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "50%",
    height: "auto",
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

const StatIcon = styled(Box)(({ theme }) => ({
  fontSize: "1.2rem",
  color: "#4b5b5d",
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.4rem",
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#4b5b5d",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: "#4b5b5d",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
  },
}));

const ChartCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #f5f0e7, #fff)",
  borderRadius: 12,
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  padding: theme.spacing(1),
  width: "45%",
  minHeight: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: theme.spacing(1),
    minHeight: "170px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(2),
    minHeight: "180px",
  },
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#4b5b5d",
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.95rem",
    marginBottom: theme.spacing(2),
  },
}));

const TotalRevenue = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 700,
  color: "#4b5b5d",
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
    marginBottom: theme.spacing(2),
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  minHeight: "200px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const LastReportDetail: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { lastReport, loading, error } = useSelector(
    (state: RootState) => state.dashboard
  );

  useEffect(() => {
    dispatch(getLastReportDetails());
  }, [dispatch]);

  // Define colors matching ViewGeneratedReport
  const colors = [
    "#4B5B5D",
    "#4682B4",
    "#B22222",
    "#A9A9A9",
    "#3CB371",
    "#FFA500",
    "#D3D3D3",
  ];

  // Prepare bar chart data based on lastReport.revenueCost
  const barData = {
    labels: lastReport?.revenueCost?.labels || [
      "Year 1",
      "Year 2",
      "Year 3",
      "Year 4",
      "Year 5",
    ],
    datasets: (lastReport?.revenueCost?.datasets || []).map((ds, index) => ({
      label: "",
      data: ds.data,
      backgroundColor: colors[index % colors.length],
      borderColor: colors[index % colors.length],
      borderWidth: 1,
      barThickness: 11,
      borderRadius: 1,
      customLabel: ds.label,
    })),
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: "bottom" as const,
        labels: {
          font: {
            size: 10,
          },
          padding: 15,
          generateLabels: (chart: any) => {
            return chart.data.datasets.map((dataset: any, index: number) => ({
              text: "",
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              lineWidth: dataset.borderWidth,
              hidden: !chart.isDatasetVisible(index),
              index,
            }));
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const dataset = context.dataset;
            const value = context.raw as number;
            const label = dataset.customLabel || "Unknown";
            return `${label}: $${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } },
        title: { display: true, text: "Years", font: { size: 12 } },
      },
      y: {
        type: "linear" as const,
        beginAtZero: true,
        grid: { color: "#e0e0e0" },
        ticks: { display: true, font: { size: 9 } },
        title: { display: true, text: "USD ($)", font: { size: 10 } },
        min: 0,
      },
    },
  };

  // Calculate total revenue for display (sum of Charging Rev + Indirect Rev over 5 years)
  // let totalRevenue = 0;
  // if (lastReport?.revenueCost?.datasets) {
  //   const chargingRevData =
  //     lastReport.revenueCost.datasets.find((ds) => ds.label === "Charging Rev")
  //       ?.data || [];
  //   const indirectRevData =
  //     lastReport.revenueCost.datasets.find((ds) => ds.label === "Indirect Rev")
  //       ?.data || [];
  //   totalRevenue =
  //     chargingRevData.reduce((a, b) => a + b, 0) +
  //     indirectRevData.reduce((a, b) => a + b, 0);
  // }

  if (loading) {
    return (
      <Container>
        <Header>Last Report Details</Header>
        <LoaderContainer>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: "#4b5b5d",
            }}
          />
          <LoaderText>Loading last report details...</LoaderText>
        </LoaderContainer>
      </Container>
    );
  }

  // Handle error state - only show error if it's not a "no data" scenario
  if (error && !error.includes("No reports found")) {
    return (
      <Container>
        <Header>Last Report Details</Header>
        <Typography color="error">Error: {error}</Typography>
      </Container>
    );
  }

  // Show no-data state if no report available
  if (!lastReport) {
    return (
      <Container>
        <Header>Last Report Details</Header>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#4b5b5d",
              marginBottom: 2,
            }}
          >
            No reports generated yet
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "#666",
              marginBottom: 3,
            }}
          >
            Generate your first report to see detailed analytics and insights
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/main/generate-reports")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(to top, #758d8e, #4b5b5d)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              color: "#fff",
              borderRadius: 8,
              padding: "12px 24px",
              "&:hover": {
                backgroundColor: "#354142",
              },
            }}
          >
            Generate Report
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Header>Last Report Details</Header>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <DetailCard>
            <DetailText>
              Report Name: {lastReport.reportName || "N/A"}
            </DetailText>
            <DetailText>
              Generated On:{" "}
              {lastReport.generatedOn
                ? new Date(lastReport.generatedOn).toLocaleDateString()
                : "N/A"}
            </DetailText>
            <DetailText>Charger Output: 22 kW</DetailText>
          </DetailCard>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <StatCard>
              <StatIcon>$</StatIcon>
              <StatValue>
                {typeof lastReport?.totalEstimatedCost === "number"
                  ? `$${lastReport?.totalEstimatedCost}`
                  : "N/A"}
              </StatValue>
              <StatLabel>Estimated Full Utility Bill</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon>⏳</StatIcon>
              <StatValue>
                {typeof lastReport.estimatedPaybackPeriods === "number"
                  ? `${lastReport.estimatedPaybackPeriods.toFixed(2)} Years`
                  : "N/A"}
              </StatValue>
              <StatLabel>Estimated Payback Period</StatLabel>
            </StatCard>
          </Box>
        </Box>
        <ChartCard sx={{ flex: 1 }}>
          <ChartTitle>5-Year Revenue & Cost Breakdown</ChartTitle>
          <TotalRevenue>
            {lastReport.netProfit > 0 ? `$${lastReport.netProfit}` : "N/A"}
          </TotalRevenue>
          <Box sx={{ width: "100%", height: "100" }}>
            <Bar data={barData} options={barOptions} />
          </Box>
        </ChartCard>
      </Box>
    </Container>
  );
};

export default LastReportDetail;
